import { createPromiseRequest, LmsMode } from "@/components/lms/lms.utils";

export interface OverviewMetricsScope {
  companySubjectId?: string;
}

interface RequiresLmsCompanySelectionParams {
  subdomain: string;
  mode: LmsMode;
  subjectOverviewMode?: boolean;
  courseId?: string;
  participantId?: string;
}

export const hasLmsCompanyScope = (
  scope: OverviewMetricsScope,
): boolean => Boolean(scope.companySubjectId);

/** Managers use the portal/header company implicitly; only admins pick a company in search. */
export const shouldShowLmsScopedContent = (
  mode: LmsMode,
  scope: OverviewMetricsScope,
): boolean => mode === "manager" || hasLmsCompanyScope(scope);

export const requiresLmsCompanySelection = ({
  subdomain,
  mode,
  subjectOverviewMode = false,
  courseId,
  participantId,
}: RequiresLmsCompanySelectionParams): boolean => {
  if (subjectOverviewMode || courseId || participantId) {
    return false;
  }

  return subdomain === "admin" && mode === "administrator";
};

interface ResolveOverviewMetricsScopeParams {
  subdomain: string;
  mode: LmsMode;
  subjectSearch: any;
  selectedChildCompanySubjectId?: string | number;
}

interface CountRequestConfig {
  shouldNotImpersonate: boolean;
  additionalQueryParams?: Record<string, string | number>;
}

interface UniqueCourseCountConfig extends CountRequestConfig {
  participantId: string | number;
}

const countUniqueCourseIdsFromEnrolments = ({
  shouldNotImpersonate,
  additionalQueryParams = {},
}: CountRequestConfig) =>
  createPromiseRequest({
    entity: "enrolment",
    method: "get",
    shouldNotImpersonate,
    getAllPages: true,
    parametersToRender: {
      ...additionalQueryParams,
    },
  })
    .then((res: any) => {
      const uniqueCourseIds = new Set<string>();

      for (const enrolment of res?.data?.results ?? []) {
        const courseId = enrolment?.course?.id;
        if (!courseId) {
          continue;
        }
        uniqueCourseIds.add(String(courseId));
      }

      return uniqueCourseIds.size;
    })
    .catch(() => 0);

const hasNonEmptyObject = (value: any) =>
  Boolean(value && typeof value === "object" && Object.keys(value).length > 0);

export const isCompanySubjectSearchSelection = (subjectSearch: any) =>
  hasNonEmptyObject(subjectSearch) &&
  ("companyId" in subjectSearch || subjectSearch?.is_company === true);

export interface EnrolmentPresetOption {
  value: string;
  label: string;
}

export const buildEnrolmentPresetsFromSubjectSearch = (
  subjectSearch: any,
): {
  presetParticipants: EnrolmentPresetOption[];
  presetCompanies: EnrolmentPresetOption[];
} => {
  if (!hasNonEmptyObject(subjectSearch) || !subjectSearch?.label) {
    return { presetParticipants: [], presetCompanies: [] };
  }

  const option: EnrolmentPresetOption = {
    value: String(subjectSearch.value),
    label: String(subjectSearch.label),
  };

  if (isCompanySubjectSearchSelection(subjectSearch)) {
    return { presetParticipants: [], presetCompanies: [option] };
  }

  return { presetParticipants: [], presetCompanies: [] };
};

export const getSubjectSearchParticipantId = (
  subjectSearch: any,
): string | undefined => {
  if (!hasNonEmptyObject(subjectSearch)) {
    return undefined;
  }

  if (isCompanySubjectSearchSelection(subjectSearch)) {
    return undefined;
  }

  if (!subjectSearch?.value) {
    return undefined;
  }

  return String(subjectSearch.value);
};

export const getSubjectSearchCompanySubjectId = (
  subjectSearch: any,
): string | undefined => {
  if (!isCompanySubjectSearchSelection(subjectSearch) || !subjectSearch?.value) {
    return undefined;
  }

  return String(subjectSearch.value);
};

export const resolveOverviewMetricsScope = ({
  subdomain,
  mode,
  subjectSearch,
  selectedChildCompanySubjectId,
}: ResolveOverviewMetricsScopeParams): OverviewMetricsScope => {
  const selectedCompanySubjectId = getSubjectSearchCompanySubjectId(subjectSearch);

  if (subdomain === "admin" && mode === "administrator") {
    if (selectedCompanySubjectId) {
      return { companySubjectId: selectedCompanySubjectId };
    }

    return {};
  }

  if (subdomain === "manage" && mode === "manager") {
    if (selectedChildCompanySubjectId) {
      return { companySubjectId: String(selectedChildCompanySubjectId) };
    }

    return {};
  }

  return {};
};

export const fetchEntityCount = ({
  entity,
  shouldNotImpersonate,
  additionalQueryParams = {},
  rejectOnError = false,
}: {
  entity: string;
  shouldNotImpersonate: boolean;
  additionalQueryParams?: Record<string, string | number>;
  rejectOnError?: boolean;
}) =>
  createPromiseRequest({
    entity,
    method: "get",
    shouldNotImpersonate,
    parametersToRender: {
      limit: 1,
      ...additionalQueryParams,
    },
  })
    .then((res: any) => Number(res?.data?.count ?? 0))
    .catch((requestError: any) => {
      if (rejectOnError) {
        throw requestError;
      }

      return 0;
    });

export const countUniqueEnrolledCoursesForParticipant = ({
  participantId,
  shouldNotImpersonate,
  additionalQueryParams = {},
}: UniqueCourseCountConfig) =>
  countUniqueCourseIdsFromEnrolments({
    shouldNotImpersonate,
    additionalQueryParams: {
      participant_id: participantId,
      ...additionalQueryParams,
    },
  });

export const countUniqueCoursesWithEnrolments = ({
  shouldNotImpersonate,
  additionalQueryParams = {},
}: CountRequestConfig) =>
  countUniqueCourseIdsFromEnrolments({
    shouldNotImpersonate,
    additionalQueryParams,
  });
